import { useEffect, useState } from 'react';
import { styled, alpha } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  Stack,
  Tooltip,
  Box,
  Chip,
  InputBase,
  TextField
} from '@mui/material';
import { getRequest } from '../../../redux/modules/radiology/slice';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { USER_ROLE_DEFAULT } from '../../../constants/Constant';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import LinkDicomDialog from '../../../components/LinkDicomDialog';

// Modern styled components
const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: '4px 16px',
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: 400,
  transition: 'all 200ms ease',
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    width: '100%',
  },
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(1),
  fontSize: '0.9375rem',
  '& input': {
    padding: '8px 0',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: theme.palette.background.paper,
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.dark,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '14px 16px',
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.875rem',
    padding: '12px 16px',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 150ms ease',
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme, statustype }) => {
  const colors = {
    complete: {
      bg: alpha(theme.palette.success.main, 0.1),
      color: theme.palette.success.dark,
      border: alpha(theme.palette.success.main, 0.3),
    },
    miss: {
      bg: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.dark,
      border: alpha(theme.palette.error.main, 0.3),
    },
    requested: {
      bg: alpha(theme.palette.warning.main, 0.1),
      color: theme.palette.warning.dark,
      border: alpha(theme.palette.warning.main, 0.3),
    },
    pending: {
      bg: alpha(theme.palette.info.main, 0.1),
      color: theme.palette.info.dark,
      border: alpha(theme.palette.info.main, 0.3),
    },
    default: {
      bg: alpha(theme.palette.grey[500], 0.1),
      color: theme.palette.grey[700],
      border: alpha(theme.palette.grey[500], 0.3),
    },
  };

  const statusColors = colors[statustype] || colors.default;

  return {
    backgroundColor: statusColors.bg,
    color: statusColors.color,
    border: `1px solid ${statusColors.border}`,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 26,
    '& .MuiChip-label': {
      padding: '0 10px',
    },
  };
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.dark,
  padding: 8,
  borderRadius: 8,
  transition: 'all 150ms ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
}));

const StyledPagination = styled(Pagination)(({ theme }) => ({
  marginTop: theme.spacing(3),
  '& .MuiPaginationItem-root': {
    borderRadius: 8,
    fontWeight: 500,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

const tableHeading = ["Appointment Date & Time", "Order Id", "Patient Name", "Exams", "Appointment Status", "Report Status", "Category", "Action"];

let limit = 10;
let pageCount = 0;

export default function RadiologyList({ setItemData, setShowComponent, itemData }) {
  const dispatch = useDispatch();
  let { getRes } = useSelector(state => state.radiology);
  let { currentBranch } = useSelector(state => state.branch);
  let { userData } = useSelector(state => state.auth);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Link DICOM dialog state
  const [linkDicomDialogOpen, setLinkDicomDialogOpen] = useState(false);
  const [selectedAppointmentForLink, setSelectedAppointmentForLink] = useState(null);

  const openLinkDicomDialog = (appointment) => {
    setSelectedAppointmentForLink(appointment);
    setLinkDicomDialogOpen(true);
  };

  const handleLinkSuccess = () => {
    // Refresh the list after successful link
    dispatch(getRequest({ page, limit, searchQuery: debouncedSearch, dateFrom, dateTo }));
  };

  // Check if user can edit a report based on status and ownership
  const canEditReport = (report) => {
    if (!report) return true; // No report yet = can create
    if (userData.role === 'admin') return true; // Admin can always edit
    if (report.reportStatus === 'completed') return false; // Completed reports can't be edited by non-admins
    // Pending reports can only be edited by the creator
    return report.createdBy === userData._id;
  };

  const filteredTableHeadings = userData.role !== USER_ROLE_DEFAULT.PATIENT
    ? tableHeading
    : tableHeading.filter(column =>
      column !== 'Patient Name' && column !== 'Appointment Status' && column !== 'Category' && column !== 'Report Status'
    );

  const filteredElem = userData.role !== USER_ROLE_DEFAULT.PATIENT
    ? getRes?.data
    : getRes && getRes?.data?.filter((data) => data?.orderId?.status === "Appointment Complete");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!currentBranch) { return }
    dispatch(getRequest({ page, limit, searchQuery: debouncedSearch, dateFrom, dateTo }))
  }, [dispatch, currentBranch, page, debouncedSearch, dateFrom, dateTo]);

  if (getRes?.dataCount && getRes?.dataCount > limit) {
    pageCount = getRes?.dataCount / limit;
    if (pageCount % 2 !== 0) {
      pageCount = Math.ceil(pageCount);
    }
  }

  const changeComponent = (componentName, item) => {
    setShowComponent(componentName);
    setItemData({
      radiologyId: item._id,
      orthancPatientId: item.orthancPatientId,
      examList: item?.examList,
      reportData: item?.reportId,
      seletedpatient: item?.patientId,
      lmp: item?.appoinmentId?.lmp,
      examReason: item?.appoinmentId?.examReason,
      orderId: item?.orderId?.orderId,
      appointmentDate: item?.appoinmentId?.startTime,
      referringDoctor: `${item?.referringSignatureId?.firstName} ${item?.referringSignatureId?.lastName}`,
      studyData: {
        orthancStudyID: item?.orderId?.orthancStudyID,
        orthancStudyDescription: item?.orderId?.orthancStudyDescription,
        orthancStudyInstanceUID: item?.orderId?.orthancStudyInstanceUID,
        dicomComments: item?.orderId?.dicomComments,
        structuredReports: item?.orderId?.structuredReports,
        encapsulatedPDFs: item?.orderId?.encapsulatedPDFs,
        secondaryCaptureCount: item?.orderId?.secondaryCaptureCount,
        secondaryCaptureInstances: item?.orderId?.secondaryCaptureInstances,
      }
    });
  };

  // Map internal status to display label
  const getStatusDisplayLabel = (status) => {
    const statusMap = {
      'scheduled': 'Scheduled',
      'in_progress': 'In Progress',
      'images_received': 'Images Received',
      'report_pending': 'Report Pending',
      'report_complete': 'Report Complete',
      'Requested': 'Requested',
      'Appointment Complete': 'Appointment Complete',
      'Appointment Miss': 'Appointment Miss',
    };
    return statusMap[status] || status;
  };

  const getAppointmentStatusType = (status) => {
    if (status === "Appointment Miss") return "miss";
    if (status === "Appointment Complete" || status === "report_complete") return "complete";
    if (status === "Requested" || status === "scheduled") return "requested";
    if (status === "report_pending" || status === "in_progress") return "pending";
    if (status === "images_received") return "complete";
    return "default";
  };

  // Check if order allows report actions (completed appointment or already has report in progress)
  const canShowReportActions = (status) => {
    return ["Appointment Complete", "report_pending", "report_complete", "images_received"].includes(status);
  };

  const getReportStatusType = (status) => {
    if (status === "completed") return "complete";
    if (status === "pending") return "pending";
    return "default";
  };

  return (
    <>
      <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
        <SearchContainer>
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <SearchInput
            placeholder="Search by patient name, order ID..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </SearchContainer>
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <TextField
            type="date"
            size="small"
            label="From"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateFrom || undefined }}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }}
          />
          {(dateFrom || dateTo) && (
            <Tooltip title="Clear date filter" arrow>
              <IconButton
                size="small"
                onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
                sx={{ color: 'text.secondary' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
      <StyledTableContainer component={Paper}>
        <Table sx={{ minWidth: { xs: 700, md: 900 } }} aria-label="imaging studies table">
          <TableHead>
            <TableRow>
              {filteredTableHeadings?.map((elem) => (
                <StyledTableCell key={elem}>{elem}</StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredElem?.length > 0 ? (
              filteredElem?.map((appointment) => (
                <StyledTableRow key={appointment._id}>
                  <StyledTableCell>
                    <Typography sx={{ fontWeight: 500 }}>
                      {moment(appointment?.appoinmentId?.startTime).format('MM-DD-YYYY')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {moment(appointment?.appoinmentId?.startTime).format('hh:mm A')}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ fontWeight: 600, fontFamily: 'monospace', color: 'primary.dark' }}>
                      {appointment?.orderId?.orderId}
                    </Typography>
                  </StyledTableCell>
                  {userData.role !== USER_ROLE_DEFAULT.PATIENT && (
                    <StyledTableCell>
                      <Typography sx={{ fontWeight: 500 }}>
                        {`${appointment?.patientId?.fName} ${appointment?.patientId?.lName}`}
                      </Typography>
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    <Typography sx={{ fontSize: '0.8125rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {appointment.examList.map((item) => item.list.map((listItem) => listItem?.name)).flat().join(", ")}
                    </Typography>
                  </StyledTableCell>
                  {userData.role !== USER_ROLE_DEFAULT.PATIENT && (
                    <StyledTableCell>
                      {appointment?.orderId?.status && (
                        <StatusChip
                          label={getStatusDisplayLabel(appointment?.orderId?.status)}
                          statustype={getAppointmentStatusType(appointment?.orderId?.status)}
                          size="small"
                        />
                      )}
                    </StyledTableCell>
                  )}
                  {userData.role !== USER_ROLE_DEFAULT.PATIENT && (
                    <StyledTableCell>
                      {appointment?.reportId?.reportStatus ? (
                        <StatusChip
                          label={appointment?.reportId?.reportStatus}
                          statustype={getReportStatusType(appointment?.reportId?.reportStatus)}
                          size="small"
                        />
                      ) : (
                        <Typography sx={{ color: 'text.disabled' }}>-</Typography>
                      )}
                    </StyledTableCell>
                  )}
                  {userData.role !== USER_ROLE_DEFAULT.PATIENT && (
                    <StyledTableCell>
                      <Typography>{appointment?.appoinmentId?.appoinmentCategory || '-'}</Typography>
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {canShowReportActions(appointment?.orderId?.status) ? (
                        <>
                          {(userData.role !== USER_ROLE_DEFAULT.PATIENT || userData.canViewImages) && appointment?.orderId?.orthancStudyInstanceUID && (
                            <Tooltip title="Open in Viewer (New Tab)" arrow placement="top">
                              <ActionButton
                                component="a"
                                href={`${process.env.REACT_APP_OHIF_VIEWER_URL || 'http://localhost:3001'}/viewer?StudyInstanceUIDs=${appointment?.orderId?.orthancStudyInstanceUID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </ActionButton>
                            </Tooltip>
                          )}
                          {/* Link DICOM button - only shows when no DICOM is attached */}
                          {userData.role !== USER_ROLE_DEFAULT.PATIENT && !appointment?.orderId?.orthancStudyInstanceUID && (
                            <Tooltip title="Link DICOM Study" arrow placement="top">
                              <ActionButton
                                onClick={() => openLinkDicomDialog(appointment)}
                                sx={{ color: 'warning.main' }}
                              >
                                <LinkIcon fontSize="small" />
                              </ActionButton>
                            </Tooltip>
                          )}
                          {userData.role === USER_ROLE_DEFAULT.PATIENT && (
                            <Tooltip title="View Report" arrow placement="top">
                              <ActionButton onClick={() => { changeComponent("createReport", appointment) }}>
                                <VisibilityIcon fontSize="small" />
                              </ActionButton>
                            </Tooltip>
                          )}
                          {appointment.reportId ? (
                            <>
                              {userData.role !== USER_ROLE_DEFAULT.PATIENT && canEditReport(appointment.reportId) && (
                                <Tooltip title="Edit Report" arrow placement="top">
                                  <ActionButton onClick={() => { changeComponent("createReport", appointment) }}>
                                    <EditIcon fontSize="small" />
                                  </ActionButton>
                                </Tooltip>
                              )}
                              {userData.role !== USER_ROLE_DEFAULT.PATIENT && !canEditReport(appointment.reportId) && (
                                <Tooltip title="View Report (Read-only)" arrow placement="top">
                                  <ActionButton onClick={() => { changeComponent("viewReport", appointment) }}>
                                    <VisibilityIcon fontSize="small" />
                                  </ActionButton>
                                </Tooltip>
                              )}
                              {appointment?.reportId?.reportStatus === "completed" && (
                                <Tooltip title="View Report" arrow placement="top">
                                  <ActionButton onClick={() => { changeComponent("viewReport", appointment) }}>
                                    <SummarizeIcon fontSize="small" />
                                  </ActionButton>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              {userData.role !== USER_ROLE_DEFAULT.PATIENT && (
                                <Tooltip title="Create Report" arrow placement="top">
                                  <ActionButton onClick={() => { changeComponent("createReport", appointment) }}>
                                    <AddIcon fontSize="small" />
                                  </ActionButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <Typography sx={{ color: 'text.disabled' }}>-</Typography>
                      )}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={filteredTableHeadings.length}>
                  <EmptyState>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>No imaging studies found</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      Studies will appear here once appointments are completed
                    </Typography>
                  </EmptyState>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      </Box>

      {(getRes?.dataCount > limit) && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <StyledPagination
            count={pageCount}
            page={page}
            onChange={(event, value) => { setPage(value); }}
            color="primary"
          />
        </Box>
      )}

      {/* Link DICOM Dialog */}
      <LinkDicomDialog
        open={linkDicomDialogOpen}
        onClose={() => setLinkDicomDialogOpen(false)}
        appointment={selectedAppointmentForLink}
        onSuccess={handleLinkSuccess}
      />
    </>
  );
}
